import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Hand } from 'lucide-react';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';
import { cardTheme } from '../../styles/cardTheme';
import TeacherCard, { TeacherCardData } from './TeacherCard';

interface TeachersSectionProps {
  id?: string;
  className?: string;
  hint?: string;
  title?: string;
  subtitle?: string;
  badge?: string;
  /** When true, renders inner layout only (for HomeSectionCard wrapper) */
  embedded?: boolean;
  /** Base path for teacher links — default `/teacher/` */
  teacherPathPrefix?: string;
}

function getTeacherSpeciality(name: string, speciality: string): string {
  const teacherName = name.toLowerCase();
  if (teacherName.includes('سعيد')) return 'المواد العربية';
  if (teacherName.includes('ياسر')) return 'المواد الشرعية';
  return speciality;
}

const TeachersSkeleton: React.FC = () => (
  <div
    className="mx-auto grid w-full max-w-5xl grid-cols-1 items-stretch justify-center gap-6 sm:grid-cols-2 md:gap-7 lg:grid-cols-3"
    aria-hidden
  >
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className={`mx-auto flex h-full w-full max-w-[20rem] animate-pulse flex-col sm:mx-0 sm:max-w-none ${cardTheme.shell}`}
      >
        <div className="aspect-[3/4] w-full bg-gray-200" />
        <div className="flex flex-1 flex-col space-y-3 border-t-4 border-t-gold-500 p-4 sm:p-5">
          <div className="h-5 w-3/4 rounded bg-gray-200" />
          <div className="h-4 w-full rounded bg-gray-200" />
          <div className="h-4 w-5/6 rounded bg-gray-200" />
          <div className="mt-auto h-11 w-full rounded-xl bg-gray-200" />
        </div>
      </div>
    ))}
  </div>
);

const TeachersSection: React.FC<TeachersSectionProps> = ({
  id = 'teachers-section',
  className = 'relative py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-green-50/40 via-white to-gold-50/30',
  hint,
  title = 'مدرسين نهج الأزهر',
  subtitle = 'اختَر مدرّسك وابدأ رحلتك التعليمية في المواد العربية والشرعية والثقافية.',
  badge = 'المدرسون والمواد',
  embedded = false,
  teacherPathPrefix = '/teacher/',
}) => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState<TeacherCardData[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  const handleTeacherClick = useCallback(
    (teacherId: string) => {
      const prefix = teacherPathPrefix.endsWith('/')
        ? teacherPathPrefix
        : `${teacherPathPrefix}/`;
      navigate(`${prefix}${teacherId}`);
    },
    [navigate, teacherPathPrefix]
  );

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        setLoadingTeachers(true);
        const response = await axios.get<{
          success: boolean;
          data?: Array<{
            id: string;
            fullName: string;
            imagePath?: string;
            department?: string;
            biography?: string;
            yearsOfExperience?: number;
            active: boolean;
          }>;
        }>(`${BASE_URL}api/Teacher`, {
          params: { active: true },
          headers: { ...getTenantHeaders() },
        });

        if (response.data?.success && response.data?.data) {
          const teachersData = response.data.data
            .filter((teacher) => teacher.active)
            .map((teacher) => {
              const imageUrl = teacher.imagePath?.startsWith('http')
                ? teacher.imagePath
                : teacher.imagePath
                ? `${BASE_URL}${teacher.imagePath.startsWith('/') ? teacher.imagePath.slice(1) : teacher.imagePath}`
                : '/default-teacher.jpg';

              return {
                id: teacher.id,
                name: teacher.fullName,
                image: imageUrl,
                speciality: getTeacherSpeciality(
                  teacher.fullName,
                  teacher.department || 'مدرس متخصص'
                ),
                biography: teacher.biography,
              };
            });

          setTeachers(teachersData);
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
      } finally {
        setLoadingTeachers(false);
      }
    };

    fetchTeachers();
  }, []);

  const content = (
    <div className={embedded ? 'w-full' : 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8'}>
      <div className="mb-8 max-w-3xl md:mb-10">
        <div className="mb-3 flex items-center gap-2.5">
          <span className="inline-block h-5 w-1 shrink-0 rounded-full bg-gold-500" aria-hidden />
          <p className="text-sm font-semibold text-gold-700">{badge}</p>
        </div>
        <h2
          id="azhar-teachers-heading"
          className={`text-[clamp(1.6rem,1.5vw+1.1rem,2.35rem)] font-extrabold font-cairo-bold ${cardTheme.title}`}
        >
          {title}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base sm:leading-7">
          {subtitle}
        </p>
        {hint && (
          <p className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-gold-200 bg-gold-50 px-4 py-3 text-sm font-semibold leading-relaxed text-gold-800 shadow-sm">
            <Hand className="h-5 w-5 shrink-0 text-gold-600" />
            {hint}
          </p>
        )}
      </div>

      {loadingTeachers ? (
        <TeachersSkeleton />
      ) : teachers.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-gray-500">لا يوجد مدرسين متاحين حالياً</p>
        </div>
      ) : (
        <>
          <div
            className="mx-auto grid w-full max-w-5xl grid-cols-1 items-stretch justify-center gap-6 sm:grid-cols-2 md:gap-7 lg:grid-cols-3 [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-[20rem] sm:[&>*]:mx-0 sm:[&>*]:max-w-none"
            role="list"
            aria-label="المدرسون"
          >
            {teachers.map((teacher) => (
              <TeacherCard
                key={teacher.id}
                teacher={teacher}
                onClick={handleTeacherClick}
                teacherPathPrefix={teacherPathPrefix}
              />
            ))}
          </div>
          <div aria-live="polite" className="sr-only">
            معروض {teachers.length} من {teachers.length} مدرسين
          </div>
        </>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div id={id} className="relative z-10">
        {content}
      </div>
    );
  }

  return (
    <section id={id} className={`relative z-10 ${className}`}>
      {content}
    </section>
  );
};

export default TeachersSection;
